// src/components/MFA/MFASetupModal.tsx
import { FC, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { mfaService } from '../api';
import Modal from 'shared/ui/Modal';
import Button, { Button_Style, Button_Type } from 'shared/ui/Button/Button';
import './MFASetupModal.scss';
import CodeInput from "features/mfa/components/CodeInput/CodeInput";
import { UserContext } from "features/account";
import { ReactComponent as PlayStore } from "assets/img/Play_Store.svg";
import { ReactComponent as AppStore } from "assets/img/Apple_Store.svg";
import { APPLE_STORE_LINK, PLAY_STORE_LINK } from "utils/utils";
import Lottie from "lottie-react";

// Import Lottie animations
import emailAnimation from "assets/lottie/Email.json";
import scanQrAnimation from "assets/lottie/Scan QR code.json";
import notificationAnimation from "assets/lottie/Notification.json";
import loadingAnimation from "assets/lottie/Loading.json";
import successAnimation from "assets/lottie/Success.json";
// import errorAnimation from "assets/lottie/Error.json";

interface MFASetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userEmail: string;
  fromLogin?: boolean;
  mfaToken?: string;
}

type MFAType = 'email' | 'mobile' | 'idprotect';

const MFASetupModal: FC<MFASetupModalProps> = ({
                                                 isOpen,
                                                 onClose,
                                                 onSuccess,
                                                 userEmail,
                                                 fromLogin = false,
                                                 mfaToken = ''
                                               }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'choose' | 'setup' | 'verify'>('choose');
  const [mfaType, setMfaType] = useState<MFAType>('email');
  const [code, setCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const { user } = useContext(UserContext);
  const type = user?.type ?? 'individual';

  // Refs to prevent duplicate calls
  const isVerifying = useRef(false);
  const hasCompleted = useRef(false);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Redirection helper when fromLogin is true
  const redirectToMFAPage = (methodType: MFAType) => {
    if (methodType === 'email') {
      navigate('/mfa/verify', {
        state: { mfaToken, mfaType: 'email', mfaEmail: userEmail }
      });
    } else if (methodType === 'mobile') {
      navigate('/mfa/verify-code', {
        state: { mfaToken, mfaType: 'mobile', mfaEmail: userEmail }
      });
    } else if (methodType === 'idprotect') {
      navigate('/mfa/waiting', {
        state: { mfaToken, mfaType: 'idprotect', mfaEmail: userEmail }
      });
    }
    onClose();
  };

  const handleSetupEmail = async () => {
    if (loading) return;

    if (fromLogin) {
      redirectToMFAPage('email');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await mfaService.setupEmailMFA(type);
      setStep('verify');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupMobile = async () => {
    if (loading) return;

    if (fromLogin) {
      redirectToMFAPage('mobile');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await mfaService.setupMobileMFA(type);
      setQrCode(response.qrCode || '');
      setSecret(response.secret || '');
      setStep('setup');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupIdProtect = async () => {
    if (loading) return;

    if (fromLogin) {
      redirectToMFAPage('idprotect');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await mfaService.setupIdProtectMFA(type);

      if (response.hasToken) {
        setStep('setup');
        setWaitingForConfirmation(true);
        startPollingForConfirmation();
      } else {
        setStep('setup');
        setError("Application pas encore installé sur ce profil");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const startPollingForConfirmation = () => {
    const interval = setInterval(async () => {
      try {
        const response = await mfaService.checkIdProtectConfirmation(type);
        if (response.confirmed) {
          if (pollingInterval) clearInterval(pollingInterval);
          setWaitingForConfirmation(false);
          const enableResponse = await mfaService.enableMFA('idprotect', '', undefined, type);
          setBackupCodes(enableResponse.backupCodes);
          hasCompleted.current = true;
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    setPollingInterval(interval);
  };

  const handleVerify = useCallback(async () => {
    if (isVerifying.current || hasCompleted.current) return;
    if (!code || code.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    isVerifying.current = true;
    setLoading(true);
    setError('');

    try {
      const response = await mfaService.enableMFA(mfaType, code, mfaType === 'mobile' ? secret : undefined, type);
      if (response?.backupCodes) {
        setBackupCodes(response.backupCodes);
        hasCompleted.current = true;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
      setTimeout(() => {
        isVerifying.current = false;
      }, 1000);
    }
  }, [code, mfaType, secret, type]);

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleComplete = () => {
    onSuccess();
    onClose();
  };

  const resetModal = () => {
    setStep('choose');
    setCode('');
    setError('');
    setBackupCodes([]);
    setCopied(false);
    setWaitingForConfirmation(false);
    isVerifying.current = false;
    hasCompleted.current = false;
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleMethodSelect = (selectedType: MFAType) => {
    setMfaType(selectedType);
    if (selectedType === 'email') {
      handleSetupEmail();
    } else if (selectedType === 'mobile') {
      handleSetupMobile();
    } else if (selectedType === 'idprotect') {
      handleSetupIdProtect();
    }
  };

  const handleDownloadAppStore = () => {
    window.open(`${APPLE_STORE_LINK}`, '_blank');
  };

  const handleDownloadGooglePlay = () => {
    window.open(`${PLAY_STORE_LINK}`, '_blank');
  };

  const renderChooseMethod = () => (
    <div className="mfa-setup-choose">
      <div className="methods-list">
        {/* Méthode Email */}
        <div
          className={`method-item ${mfaType === 'email' ? 'selected' : ''}`}
          onClick={() => handleMethodSelect('email')}
        >
          <div className="method-icon email">
            <Lottie animationData={emailAnimation} loop style={{ width: 48, height: 48 }} />
          </div>
          <div className="method-content">
            <h4>Email</h4>
            <p>Recevez un code de vérification par email à chaque connexion</p>
          </div>
        </div>

        {/* Méthode Application mobile (TOTP) */}
        <div
          className={`method-item ${mfaType === 'mobile' ? 'selected' : ''}`}
          onClick={() => handleMethodSelect('mobile')}
        >
          <div className="method-icon mobile">
            <Lottie animationData={scanQrAnimation} loop style={{ width: 48, height: 48 }} />
          </div>
          <div className="method-content">
            <h4>Application tiers</h4>
            <p>Utilisez Google Authenticator, Microsoft Authenticator, etc.</p>
          </div>
        </div>

        {/* Méthode ID Protect */}
        <div
          className={`method-item ${mfaType === 'idprotect' ? 'selected' : ''}`}
          onClick={() => handleMethodSelect('idprotect')}
        >
          <div className="method-icon idprotect">
            <Lottie animationData={notificationAnimation} loop style={{ width: 48, height: 48 }} />
          </div>
          <div className="method-content">
            <h4>ID Protect</h4>
            <p>Recevez une notification push sur votre mobile ID Protect</p>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  const renderIdProtectSetup = () => (
    <div className="mfa-idprotect-setup">
      <button className="back-button" onClick={() => setStep('choose')}>
        ← Retour
      </button>

      <div className="idprotect-icon">
        <Lottie animationData={notificationAnimation} loop style={{ width: 64, height: 64 }} />
      </div>

      <h3>Application ID Protect</h3>

      <p className="description">
        Pour activer la double authentification via ID Protect,
        vous devez avoir l'application installée sur votre mobile.
      </p>

      <div className="store-buttons">
        <button className="store-btn app-store" onClick={handleDownloadAppStore}>
          <AppStore />
        </button>
        <button className="store-btn play-store" onClick={handleDownloadGooglePlay}>
          <PlayStore />
        </button>
      </div>

      <div className="help-text">
        <p>Déjà installé ?</p>
        <button
          className="retry-button"
          onClick={handleSetupIdProtect}
          disabled={loading}
        >
          J'ai installé l'application, vérifier
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  const renderIdProtectWaiting = () => (
    <div className="mfa-idprotect-waiting">
      <button className="back-button" onClick={() => setStep('choose')}>
        ← Retour
      </button>

      <div className="waiting-icon">
        <div className="pulse-ring"></div>
        <Lottie animationData={notificationAnimation} loop style={{ width: 64, height: 64 }} />
      </div>

      <h3>Confirmation sur mobile</h3>

      <p className="description">
        Une notification a été envoyée à votre application ID Protect.
        Veuillez confirmer la demande sur votre mobile.
      </p>

      <div className="loading-animation">
        <Lottie animationData={loadingAnimation} loop style={{ width: 60, height: 60 }} />
        <p className="loading-text">En attente de confirmation...</p>
      </div>

      <button
        className="resend-button"
        onClick={handleSetupIdProtect}
        disabled={loading}
      >
        Renvoyer la notification
      </button>

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  const renderMobileSetup = () => (
    <div className="mfa-mobile-setup">
      <button className="back-button" onClick={() => setStep('choose')}>
        ← Retour
      </button>

      <div className="qr-section">
        <div className="qr-code-container">
          {qrCode && <img src={qrCode} alt="QR Code" />}
        </div>
        <p>Scannez ce QR code avec votre application d'authentification</p>
      </div>

      <div className="secret-section">
        <p className="secret-label">Ou saisissez ce code manuellement :</p>
        <div className="secret-code">
          <code>{secret}</code>
          <button
            onClick={() => navigator.clipboard.writeText(secret)}
            className="copy-secret"
          >
            copier
          </button>
        </div>
      </div>

      <div className="modal-actions">
        <Button
          type={Button_Type.PRIMARY}
          style={Button_Style.SOLID}
          label="J'ai scanné le code"
          onClick={() => setStep('verify')}
        />
      </div>
    </div>
  );

  const renderVerify = () => (
    <div className="mfa-verify">
      {backupCodes.length === 0 && (
        <button className="back-button" onClick={() => setStep(mfaType === 'email' ? 'choose' : 'setup')}>
          ← Retour
        </button>
      )}

      <div className="verify-icon">
        <Lottie animationData={emailAnimation} loop style={{ width: 64, height: 64 }} />
      </div>

      <h3>Vérification en deux étapes</h3>

      {mfaType === 'email' && (
        <p className="verify-description">
          Un code à 6 chiffres a été envoyé à <strong>{userEmail}</strong>
        </p>
      )}

      {mfaType === 'mobile' && (
        <p className="verify-description">
          Entrez le code à 6 chiffres généré par votre application d'authentification
        </p>
      )}

      {backupCodes.length === 0 && (
        <CodeInput
          value={code}
          onChange={setCode}
          length={6}
          error={error}
          disabled={loading}
          autoFocus={true}
          placeholder="Entrez le code à 6 chiffres"
          onComplete={(completedCode) => {
            if (!hasCompleted.current && completedCode.length === 6 && !loading) {
              handleVerify();
            }
          }}
        />
      )}

      {backupCodes.length > 0 ? (
        <div className="backup-codes-section">
          <div className="alert-warning">
            <div>
              <strong>Codes de secours</strong>
              <p>Conservez ces codes dans un endroit sûr. Ils vous permettront d'accéder à votre compte si vous perdez l'accès à votre méthode MFA.</p>
            </div>
          </div>

          <div className="codes-grid">
            {backupCodes.map((backupCode, index) => (
              <div key={index} className="backup-code">{backupCode}</div>
            ))}
          </div>

          <div className="backup-actions">
            <Button
              type={Button_Type.PRIMARY}
              style={Button_Style.OUTLINED}
              label={copied ? "Copié !" : "Copier les codes"}
              onClick={handleCopyCodes}
            />
            <Button
              type={Button_Type.PRIMARY}
              style={Button_Style.SOLID}
              label="Terminer"
              onClick={handleComplete}
            />
          </div>
        </div>
      ) : (
        <>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <Button
              type={Button_Type.PRIMARY}
              style={Button_Style.SOLID}
              label="Vérifier"
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} infoModal={true}>
      {step === 'choose' && renderChooseMethod()}
      {step === 'setup' && mfaType === 'mobile' && renderMobileSetup()}
      {step === 'setup' && mfaType === 'idprotect' && !waitingForConfirmation && renderIdProtectSetup()}
      {step === 'setup' && mfaType === 'idprotect' && waitingForConfirmation && renderIdProtectWaiting()}
      {step === 'verify' && renderVerify()}
    </Modal>
  );
};

export default MFASetupModal;